/**
 * Route table (docs/ARCHITECTURE.md §12). No router library: `RouterProvider`/`matchPath` from
 * `shell/router.tsx` do the work. `/presenter/:sessionId` lazy-loads `presenter/PresenterPage.tsx` (another
 * task's file) and falls back to a placeholder if the import ever fails.
 */
import { lazy, Suspense } from 'react';
import { RouterProvider, useRouter, matchPath } from './shell/router.tsx';
import { strings } from './shell/strings.ts';
import { Home } from './pages/Home.tsx';
import { MomentumEntry } from './pages/MomentumEntry.tsx';
import { CreateSession } from './pages/CreateSession.tsx';
import { JoinSession } from './pages/JoinSession.tsx';
import { CodeEntry } from './pages/CodeEntry.tsx';
import { DisplayPage } from './pages/DisplayPage.tsx';
import { SessionPage } from './session/SessionPage.tsx';
import { PresenterEntry } from './presenter/PresenterEntry.tsx';
import type { PresenterPageProps } from './presenter/PresenterPage.tsx';
import { BoardTransitionDemo } from './session/BoardTransitionDemo.tsx';
import { HostEntry } from './pages/HostEntry.tsx';

function MissingPresenter({ sessionId }: PresenterPageProps) {
  return (
    <div className="page stack">
      <h1>{strings.presenter.banner}</h1>
      <p>{strings.presenter.missing}</p>
      <p className="text-muted">Session: {sessionId}</p>
      <footer className="footer">{strings.footer.conceptPrototype}</footer>
    </div>
  );
}

const LazyPresenterPage = lazy(() => import('./presenter/PresenterPage.tsx').catch(() => ({ default: MissingPresenter })));
const LazyHotPotatoPreview = lazy(() => import('./dev/HotPotatoPreview.tsx').then(module => ({ default: module.HotPotatoPreview })));

function NotFound() {
  return (
    <div className="page stack">
      <h1>404</h1>
      <p>{strings.common.error}</p>
      <footer className="footer">{strings.footer.conceptPrototype}</footer>
    </div>
  );
}

function AppRoutes() {
  const { pathname } = useRouter();

  if (matchPath('/', pathname)) return <Home />;
  if (matchPath('/momentum', pathname)) return <MomentumEntry />;
  if (matchPath('/create', pathname)) return <CreateSession />;
  if (matchPath('/code', pathname)) return <CodeEntry />;
  if (matchPath('/host', pathname)) return <HostEntry />;

  const join = matchPath('/join/:inviteToken', pathname);
  if (join) return <JoinSession key={join.params.inviteToken} inviteToken={join.params.inviteToken!} />;

  const session = matchPath('/s/:sessionId', pathname);
  if (session) return <SessionPage key={session.params.sessionId} sessionId={session.params.sessionId!} />;

  const display = matchPath('/display/:sessionId', pathname);
  if (display) return <DisplayPage key={display.params.sessionId} sessionId={display.params.sessionId!} />;

  if (matchPath('/presenter', pathname)) return <PresenterEntry />;

  // Review-only harness for the board -> mini-game transition (BoardTransition.tsx); see its own file header.
  if (matchPath('/dev/board-transition', pathname)) return <BoardTransitionDemo />;
  if (matchPath('/dev/hot-potato', pathname)) return <Suspense fallback={<p className="page">Loading visual prototype…</p>}><LazyHotPotatoPreview /></Suspense>;

  const presenter = matchPath('/presenter/:sessionId', pathname);
  if (presenter) {
    return (
      <Suspense fallback={<p className="page">{strings.session.loading}</p>}>
        <LazyPresenterPage sessionId={presenter.params.sessionId!} />
      </Suspense>
    );
  }

  return <NotFound />;
}

export function App() {
  return (
    <RouterProvider>
      <div className="app-shell">
        <AppRoutes />
      </div>
    </RouterProvider>
  );
}
